import * as yup from 'yup';

import { errors } from '../config';

/*
 * Utility validation function for required string
 */
const requiredString = (fieldName) => {
  return yup
    .string(errors.INVALID_STRING)
    .trim(errors.EMPTY_FIELD(fieldName))
    .required(errors.REQUIRED(fieldName));
};

const requiredStringWithOnlyLetters = (fieldName) => {
  return requiredString(fieldName).matches(/^[A-Za-z]+$/, errors.ONLY_LETTERS(fieldName));
};

const requiredStringWithOnlyLettersWithMinMax = (fieldName, minLength, maxLength) => {
  return requiredStringWithOnlyLetters(fieldName)
    .min(minLength, errors.MIN_LENGTH(fieldName, minLength))
    .max(maxLength, errors.MAX_LENGTH(fieldName, maxLength));
};

/*
 * Business name validator: allow letters, numbers, spaces and common special chars
 * Allowed characters: . , & ' ( ) / -
 */
const requiredBusinessName = (fieldName) =>
  requiredString(fieldName)
    .matches(new RegExp("^[A-Za-z0-9 .,&'()/-]+$"), errors.INVALID_BUSINESS_NAME(fieldName))
    .min(2, errors.MIN_LENGTH(fieldName, 2))
    .max(50, errors.MAX_LENGTH(fieldName, 50));

/*
 * Utility validation function for passwords
 */

const password = (fieldName) =>
  requiredString(fieldName)
    .min(8, errors.MIN_LENGTH(fieldName, 8))
    .max(25, errors.MAX_LENGTH(fieldName, 25))
    .matches(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[^\w]).{8,}/, errors.INVALID_PASSWORD);

const loginAuthUserSchema = yup
  .object({
    email: requiredString('Email').email(errors.INVALID_EMAIL),
    password: requiredString('Password')
  })
  .required();

/*
 * Auth User Schema
 */
const authUserSchema = yup
  .object({
    email: requiredString('Email').email(errors.INVALID_EMAIL),
    password: password('Password')
  })
  .required();
const forgotPasswordSchema = yup
  .object({
    email: requiredString('Email').email(errors.INVALID_EMAIL)
  })
  .required();

/*
 * Signup Setup Validation Schema
 */

const setupSchema = yup
  .object({
    first_name: requiredStringWithOnlyLettersWithMinMax('First name', 2, 30),
    last_name: requiredStringWithOnlyLettersWithMinMax('Last name', 2, 30),
    country_id: requiredString('Country').nullable(),
    business_name: requiredBusinessName('Business name'),
    phone_number: requiredString('Phone number').matches(
      /^(?!\b(0)\1+\b)(\+?\d{1,3}[. -]?)?\(?\d{3}\)?([. -]?)\d{3}\3\d{4}$/,
      errors.INVALID_PHONE
    ),
    yearly_revenue: requiredString('Yearly revenue').nullable()
  })
  .required();

/*
 * Two Factor Authentication Schema
 */
const twoFASchema = yup
  .object({
    code: requiredString('Access code')
  })
  .required();

/*
 * Refund details Schema (overpayment/underpayment refund form).
 * Wallet address is required; network and reason are optional.
 */
const refundDetailsSchema = yup
  .object({
    wallet_address: requiredString("Customer's wallet address"),
    wallet_network: yup.string().trim(),
    reason: yup.string().trim().max(1000, 'Reason must be at most 1000 characters')
  })
  .required();

/*
 * Business Schmea
 */
const businessSchema = yup
  .object({
    country_id: requiredString('Country').nullable(),
    phone_number: requiredString('Phone number').matches(
      /^(?!\b(0)\1+\b)(\+?\d{1,3}[. -]?)?\(?\d{3}\)?([. -]?)\d{3}\3\d{4}$/,
      errors.INVALID_PHONE
    )
  })
  .required();

/*
 * Security Schema
 */
const securitySchema = yup
  .object({
    current_password: password('Current password'),
    new_password: password('New password'),
    confirm_password: requiredString('Confirm password').oneOf(
      [yup.ref('new_password'), null],
      'Passwords do not match'
    )
  })
  .required();

/*
 * Password Reset Schema
 */
const passwordResetSchema = yup
  .object({
    password: password('New password'),
    confirm_password: requiredString('Confirm password').oneOf(
      [yup.ref('password'), null],
      'Passwords do not match'
    )
  })
  .required();

/*
 * Webhook Schema
 */
const webhookSchema = yup
  .object({
    webhook_url: requiredString('Webhook Url').url(errors.INVALID_URL)
  })
  .required();

const IPV4_REGEX =
  /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

const isValidIpv6 = (value) => {
  if (!value.includes(':')) return false;

  const parts = value.split('::');
  if (parts.length > 2) return false;

  const hexGroupRegex = /^[0-9a-fA-F]{1,4}$/;
  const allHex = (groups) => groups.every((group) => hexGroupRegex.test(group));

  if (parts.length === 1) {
    const groups = value.split(':');
    return groups.length === 8 && allHex(groups);
  }

  const leftGroups = parts[0] === '' ? [] : parts[0].split(':');
  const rightGroups = parts[1] === '' ? [] : parts[1].split(':');
  if (leftGroups.length + rightGroups.length > 7) return false;
  return allHex(leftGroups) && allHex(rightGroups);
};

const isValidIpAddress = (value) => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  // CIDR notation (e.g. "10.0.0.0/24") is not supported by the backend.
  if (trimmed.includes('/')) return false;
  return IPV4_REGEX.test(trimmed) || isValidIpv6(trimmed);
};

/*
 * Allowed Server IP Schema
 * Empty string is allowed so the merchant can clear the restriction.
 */
const allowedServerIpSchema = yup
  .object({
    allowed_server_ip: yup
      .string()
      .trim()
      .test(
        'is-valid-ip',
        'Enter a valid IPv4 or IPv6 address (CIDR notation is not supported)',
        (value) => !value || isValidIpAddress(value)
      )
  })
  .required();

/*
 * Export data Schema
 */
const exportDataSchema = yup.object({
  from: requiredString('Start date').nullable(''),
  to: requiredString('End date').nullable('')
});

/*
 * Single Transfer Schema
 */
const singleTransferSchema = yup.object({
  currency_id: yup.number().required('Please select a currency'),
  address: yup.string().when('recipient', {
    is: (value) => !value || value === '0',
    then: yup
      .string()
      .required('Wallet address is a required field.')
      .min(0, 'Wallet addresses cannot be this short!')
  }),

  recipient: yup.string().nullable(),
  amount: yup
    .number()
    .typeError('Amount is required')
    .moreThan(0, 'Enter an amount greater than zero')
});

/*
 * Single Beneficiary Schema
 */
const singleBeneficiarySchema = yup.object({
  wallet_address: requiredString('Beneficiary Wallet address'),
  alias: requiredString('Beneficiary Alias'),
  currency_id: requiredString('Currency').nullable()
});

/*
 * Bulk beneficiary Schema
 */
const SUPPORTED_FORMATS = ['text/csv'];
const bulkBeneficiarySchema = yup.object({
  csv: yup
    .mixed()
    .required('A file is required')
    .test(
      'fileFormat',
      'Unsupported File Format',
      (value) => value && SUPPORTED_FORMATS.includes(value[0]?.type)
    ),
  label: requiredString('Group Label'),
  currency_id: requiredString('Currency').nullable()
});

export const inviteTeamMemberSchema = yup.object().shape({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().required('Last name is required'),
  role_id: yup.string().required('Role is required')
});

/**
 * Onboarding Schema
 */
export const onboardingSchema = yup.object().shape({
  password: yup.string().required('Password is required').min(8, 'Password too short'),
  confirm_password: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required')
});
/*
 * Bulk transfer Schema
 */
const bulkTransferSchema = yup.object({
  beneficiaries_label: requiredString('Beneficiaries label').nullable()
});

/*
 * Add Settelement Account Schema
 */
const addSettlementAccountSchema = yup.object({
  type: requiredString('Account type').nullable(''),
  currency_id: requiredString('Currency').nullable(''),
  provider: yup.string().when('type', {
    is: (value) => value === 'FIAT',
    then: requiredString('Bank Name')
  }),
  address: requiredString('')
});

/*
 * Kyc Information Schema
 */

const kycInformationSchema = yup.object({
  type: requiredString('Kyc type'),
  country_id: requiredString('Country').nullable()
});

/*
 * Signup V2 Schema
 */
const signupV2Schema = yup
  .object({
    first_name: requiredStringWithOnlyLettersWithMinMax('First name', 2, 30),
    last_name: requiredStringWithOnlyLettersWithMinMax('Last name', 2, 30),
    email: requiredString('Email').email(errors.INVALID_EMAIL),
    password: password('Password'),
    phone_number: requiredString('Phone number').matches(
      /^(?!\b(0)\1+\b)(\+?\d{1,3}[. -]?)?\(?\d{3}\)?([. -]?)\d{3}\3\d{4}$/,
      errors.INVALID_PHONE
    ),
    account_type: requiredString('Account type'),
    terms_accepted: yup
      .boolean()
      .oneOf([true], 'You must accept the terms and conditions')
      .required('You must accept the terms and conditions')
  })
  .required();

const feeSchema = yup.object({
  type: yup.string().required('Fee type is required'),
  value: yup
    .string()
    .required('Fee value is required')
    .matches(/^\d*\.?\d+$/, 'Fee value must be a valid number')
    .test('is-positive', 'Fee value must be positive', (value) => {
      return value && parseFloat(value) > 0;
    }),
  value_type: yup.string().required('Value type is required'),
  merchant_id: yup.object().nullable()
});

export {
  addSettlementAccountSchema,
  allowedServerIpSchema,
  authUserSchema,
  bulkBeneficiarySchema,
  bulkTransferSchema,
  businessSchema,
  exportDataSchema,
  feeSchema,
  forgotPasswordSchema,
  isValidIpAddress,
  kycInformationSchema,
  loginAuthUserSchema,
  passwordResetSchema,
  refundDetailsSchema,
  securitySchema,
  setupSchema,
  signupV2Schema,
  singleBeneficiarySchema,
  singleTransferSchema,
  twoFASchema,
  webhookSchema
};
